import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";

/**
 * Compact persistent fundraising campaign bar.
 *
 * - Sits immediately below the main navigation on every public page.
 * - Reads its content from `/api/site-settings → campaign_bar` so admins
 *   edit everything (message, amounts, labels, destination, enabled flag)
 *   without a code deploy.
 * - Hidden on /admin and on the retired KB Hallen page.
 * - Progress is manual (admin-driven); it does not read raffle availability
 *   or Stripe calculations.
 */
export default function CampaignBar() {
  const { lang } = useI18n();
  const location = useLocation();
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/site-settings")
      .then(r => { if (!cancelled) setCfg(r.data?.campaign_bar || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Never render inside admin or retired routes.
  const path = location.pathname || "";
  if (path.startsWith("/admin")) return null;
  if (/^\/events\/kb-hallen-2026(?:\/|$)/i.test(path)) return null;

  if (!cfg || cfg.show === false) return null;

  // Date-driven auto-hide (optional).
  if (cfg.end_date) {
    const end = Date.parse(cfg.end_date);
    if (!Number.isNaN(end) && Date.now() > end) return null;
  }

  const pick = (field) => (field && (field[lang] || field.en || field.da || field.es)) || "";
  const message = pick(cfg.message);
  const amount = pick(cfg.amount_format);
  const remaining = pick(cfg.remaining_label);
  const ctaLabel = pick(cfg.cta_label);
  const ctaUrl = cfg.cta_url || "/raffle#buy";

  const target = Number(cfg.target_amount_dkk || 0);
  const current = Number(cfg.current_amount_dkk || 0);
  const pct = target > 0 ? Math.max(0, Math.min(100, Math.round((current / target) * 100))) : 0;

  if (!message && !amount && !ctaLabel) return null;

  // External vs internal destination.
  const isExternal = /^https?:\/\//i.test(ctaUrl);
  const CTA = isExternal ? (
    <a href={ctaUrl} data-testid="campaign-bar-cta"
       className="shrink-0 bg-terracotta text-white px-4 py-2 text-xs tracking-wide font-medium hover:bg-terracotta-dark transition-colors whitespace-nowrap">
      {ctaLabel}
    </a>
  ) : (
    <Link to={ctaUrl} data-testid="campaign-bar-cta"
          className="shrink-0 bg-terracotta text-white px-4 py-2 text-xs tracking-wide font-medium hover:bg-terracotta-dark transition-colors whitespace-nowrap">
      {ctaLabel}
    </Link>
  );

  return (
    <div data-testid="campaign-bar"
         className="w-full bg-cream border-b border-line text-ink">
      <div className="container-x py-2.5 md:py-3 flex items-center gap-3 md:gap-5">
        {/* Message + progress */}
        <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center md:gap-5">
          <p data-testid="campaign-bar-message"
             className="text-[12.5px] md:text-sm leading-snug truncate md:truncate-none md:whitespace-normal">
            {message}
          </p>
          <div className="mt-1 md:mt-0 flex items-center gap-3 min-w-0">
            {/* Numeric progress — always visible */}
            <div className="hidden md:flex items-center gap-2 text-xs text-ink/70 tabular-nums shrink-0"
                 data-testid="campaign-bar-amount">
              <span>{amount}</span>
              {remaining ? <span className="text-ink/50">·</span> : null}
              {remaining ? <span data-testid="campaign-bar-remaining">{remaining}</span> : null}
            </div>
            {/* Progress bar — subtle and compact */}
            {target > 0 && (
              <div className="hidden md:block relative h-1 w-28 bg-ink/10 rounded-full overflow-hidden"
                   role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}
                   data-testid="campaign-bar-progress">
                <div className="absolute inset-y-0 left-0 bg-terracotta transition-all"
                     style={{ width: `${pct}%` }} />
              </div>
            )}
            {/* Mobile condensed amount */}
            <div className="md:hidden text-[11px] text-ink/70 tabular-nums"
                 data-testid="campaign-bar-amount-mobile">
              {amount}
            </div>
          </div>
        </div>
        {CTA}
      </div>
    </div>
  );
}
